import * as React from 'react';

// API
import apibridge from '../../apibridge';

// PACKAGES
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { SwitchTransition, CSSTransition } from 'react-transition-group';

// UTILS
import { validationErrorsToString } from '../../utils';

// COMPONENTS
import Button from '../../components/Button';
import LoadingSpinner from '../../components/LoadingSpinner';

// TYPES
import { SafetyChecklistCommentCommand } from '../../api/models';
import { closeModalType } from '../../components/GlobalModal';

interface CommentType {
	id: string;
	closeModal?: closeModalType;
	canEdit?: boolean;
}

const initialCommentFormData: SafetyChecklistCommentCommand = {
	id: undefined,
	comment: undefined
};

const CommentModal: React.FC<CommentType> = ({ id, closeModal, canEdit }) => {
	// Error and Loading state
	const [formErrorMessage, setFormErrorMessage] = React.useState<string>('');

	const [isLoading, setIsLoading] = React.useState<boolean>(true);

	// Set up Data state
	const [commentFormData, setCommentFormData] = React.useState<SafetyChecklistCommentCommand>(initialCommentFormData);

	// Set up Read Only state
	const [readonly, setReadonly] = React.useState<boolean>(false);

	const getSafetyChecklistsDetailCommentDetailData = async () => {
		setIsLoading(true);

		const response = await apibridge.getSafetyChecklistComment(id);

		if (response.isError) {
			setIsLoading(false);

			setFormErrorMessage(
				response.validationErrors.length
					? validationErrorsToString(response.validationErrors)
					: response.message || 'An error has occurred'
			);
		} else {
			const { result = {} } = response;

			setIsLoading(false);

			if (result.comment && result.comment.length > 0) {
				setReadonly(true);
			}

			setCommentFormData(result);
		}
	};

	React.useEffect(() => {
		getSafetyChecklistsDetailCommentDetailData();
	}, []);

	const submitHandler = async (values: any) => {
		setIsLoading(true);

		setCommentFormData((prevState) => ({
			...prevState,
			comment: values.commentInput
		}));

		const response = await apibridge.postSafetyChecklistComment({
			id: id,
			comment: values.commentInput
		});

		if (response.isError) {
			setIsLoading(false);

			setFormErrorMessage(
				response.validationErrors.length
					? validationErrorsToString(response.validationErrors)
					: response.message || 'An error has occurred'
			);
		} else {
			setIsLoading(false);

			if (closeModal) {
				closeModal();
			}
		}
	};

	const readOnlyHandler = () => {
		setTimeout(() => {
			setReadonly(false);
		}, 100);
	};

	const validationSchema = Yup.object({
		commentInput: Yup.string().required('Required')
	});

	const initialValues = {
		commentInput: commentFormData.comment
	};

	return (
		<>
			{!!formErrorMessage ? (
				<div className="form-validation-error">{formErrorMessage}</div>
			) : (
				<SwitchTransition mode="out-in">
					<CSSTransition key={`key_${isLoading}`} timeout={600} classNames="standalone-fade" mountOnEnter unmountOnExit>
						{isLoading ? (
							<LoadingSpinner show={isLoading} />
						) : (
							<Formik
								initialValues={initialValues}
								validationSchema={validationSchema}
								onSubmit={submitHandler}
								enableReinitialize
							>
								{(formik) => {
									return (
										<Form translate="yes" className="general-form mtop-0">
											<div className="form-group">
												<label className="label mbtom-14">Additional comments</label>

												<div className="form-field">
													<Field
														readOnly={!canEdit}
														as="textarea"
														name="commentInput"
														id="commentInput"
														className={!canEdit ? 'form-input large readonly' : 'form-input large'}
													/>

													<ErrorMessage name="commentInput" component="div" className="error-message" />
												</div>
											</div>

											<div className="form-button-group w-100">
												{!canEdit ? (
													<Button disabled={!canEdit} onClick={() => readOnlyHandler()} className="btn-primary w-100">
														Edit comment
													</Button>
												) : (
													<Button disabled={!canEdit} type="submit" className="btn-primary w-100">
														Save comment
													</Button>
												)}
											</div>
										</Form>
									);
								}}
							</Formik>
						)}
					</CSSTransition>
				</SwitchTransition>
			)}
		</>
	);
};

export default CommentModal;
